import { Component, createEffect, createMemo, createSignal, For, onMount, Show } from 'solid-js'
import { A, useNavigate } from '@solidjs/router'
import { 
  Search, 
  MoreVertical,
  Trash2,
} from 'lucide-solid'
import { 
  chatStore, 
  characterStore, 
  i18nStore,
} from '../../store'
import { CharacterAvatar } from '../../shared/AvatarIcon'
import { setComponentPageTitle, toDuration } from '../../shared/util'
import TextInput from '../../shared/TextInput'
import { Page } from '../../Layout'
import Loading from '../../shared/Loading'
import { ConfirmModal } from '../../shared/Modal'
import { AppSchema } from '../../../common/types/schema'

export const ChatHistory: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const nav = useNavigate()
  
  const [search, setSearch] = createSignal('')
  const [activeTab, setActiveTab] = createSignal('all')
  const [showDelete, setDelete] = createSignal('')

  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.map,
    loaded: s.characters.loaded,
  }))

  const state = chatStore((s) => ({
    allChats: s.allChats,
  }))

  onMount(() => {
    setComponentPageTitle(i18n.t.chatHistory)
    characterStore.getAllChats()
  })

  const filteredChats = createMemo(() => {
    let list = state.allChats

    // Tab filtering
    if (activeTab() === 'today') {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      list = list.filter(chat => new Date(chat.updatedAt) >= today)
    }

    // Search filtering
    const query = search().toLowerCase().trim()
    if (query) {
      list = list.filter(chat => {
        const char = chars.map[chat.characterId]
        if (char?.name.toLowerCase().includes(query)) return true
        if (chat.name?.toLowerCase().includes(query)) return true
        return false
      })
    }

    return list
  })

  const groupedByCharacter = createMemo(() => {
    if (activeTab() !== 'character') return null
    
    const groups: Record<string, typeof state.allChats> = {}
    for (const chat of filteredChats()) {
      if (!groups[chat.characterId]) groups[chat.characterId] = []
      groups[chat.characterId].push(chat)
    }
    
    return Object.entries(groups).map(([charId, chats]) => ({
      char: chars.map[charId],
      chats: chats.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    })).sort((a, b) => (b.chats[0]?.updatedAt || '').localeCompare(a.chats[0]?.updatedAt || ''))
  })

  const confirmDelete = () => {
    chatStore.deleteChat(showDelete(), () => setDelete(''))
  }

  return (
    <Page class="flex flex-col gap-4 p-4 pb-20">
      {/* Search Bar */}
      <div class="relative w-full">
        <div class="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <Search size={18} class="text-[var(--bg-400)]" />
        </div>
        <input
          type="text"
          class="w-full bg-[var(--bg-900)] border border-[var(--bg-700)] rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none focus:border-[var(--hl-500)]"
          placeholder="搜索角色名..."
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
        />
      </div>

      {/* Tabs */}
      <div class="flex bg-[var(--bg-900)] p-1 rounded-lg">
        <button
          class="flex-1 py-1.5 text-sm rounded-md transition-colors"
          classList={{
            'bg-[var(--bg-700)] text-white': activeTab() === 'all',
            'text-[var(--bg-400)]': activeTab() !== 'all'
          }}
          onClick={() => setActiveTab('all')}
        >
          全部
        </button>
        <button
          class="flex-1 py-1.5 text-sm rounded-md transition-colors"
          classList={{
            'bg-[var(--bg-700)] text-white': activeTab() === 'today',
            'text-[var(--bg-400)]': activeTab() !== 'today'
          }}
          onClick={() => setActiveTab('today')}
        >
          今天
        </button>
        <button
          class="flex-1 py-1.5 text-sm rounded-md transition-colors"
          classList={{
            'bg-[var(--bg-700)] text-white': activeTab() === 'character',
            'text-[var(--bg-400)]': activeTab() !== 'character'
          }}
          onClick={() => setActiveTab('character')}
        >
          按角色
        </button>
      </div>

      {/* Chat List */}
      <Show when={chars.loaded} fallback={<div class="flex justify-center p-8"><Loading /></div>}>
        <div class="flex flex-col gap-3">
          <Show when={activeTab() !== 'character'} fallback={
            <For each={groupedByCharacter()}>
              {({ char, chats }) => (
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2 px-2 mt-2">
                    <div class="w-1 h-4 bg-[var(--hl-500)] rounded-full"></div>
                    <span class="text-sm font-bold text-[var(--bg-300)]">{char?.name || '未知角色'}</span>
                  </div>
                  <For each={chats}>
                    {(chat) => <ChatHistoryItem chat={chat} char={chars.map[chat.characterId]} onDelete={setDelete} />}
                  </For>
                </div>
              )}
            </For>
          }>
            <For each={filteredChats()}>
              {(chat) => <ChatHistoryItem chat={chat} char={chars.map[chat.characterId]} onDelete={setDelete} />}
            </For>
          </Show>
        </div>
      </Show>

      <ConfirmModal
        show={!!showDelete()}
        close={() => setDelete('')}
        confirm={confirmDelete}
        message={i18n.t.char_confirmDeleteChat}
      />
    </Page>
  )
}

const ChatHistoryItem: Component<{ 
  chat: AppSchema.Chat; 
  char: AppSchema.Character;
  onDelete: (id: string) => void;
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${month}-${day} ${hours}:${minutes}`
  }

  return (
    <div class="bg-[var(--bg-800)] border border-[var(--bg-700)] rounded-xl overflow-hidden">
      <div class="flex items-center justify-between px-4 py-2 border-b border-[var(--bg-700)]">
        <span class="text-sm font-bold text-white">和 【{props.char?.name || '未知'}】 的对话</span>
        <div class="flex items-center gap-3">
          <span class="text-xs text-[var(--bg-400)]">{formatDate(props.chat.updatedAt)}</span>
          <button class="text-[var(--bg-400)] hover:text-white" onClick={() => props.onDelete(props.chat._id)}>
            <MoreVertical size={16} />
          </button>
        </div>
      </div>
      <A href={`/chat/${props.chat._id}`} class="flex p-3 gap-3 hover:bg-[var(--bg-700)] transition-colors">
        <div class="flex-shrink-0">
          <CharacterAvatar char={props.char} format={{ size: 'lg', corners: 'rounded' }} />
        </div>
        <div class="flex flex-col min-w-0 justify-center">
          <span class="text-sm font-medium text-pink-400 mb-1">{props.char?.name}</span>
          <p class="text-xs text-[var(--bg-300)] line-clamp-2 leading-relaxed">
            {props.chat.name || "开始对话吧..."}
          </p>
        </div>
      </A>
    </div>
  )
}

export default ChatHistory








